/**
 * Persists the current route and in-progress store so a refresh keeps working data.
 */
import { readJwtUserId } from "./cache";
import { LAST_PATH_KEY, SESSION_RECORD_KEY, STORE_SESSION } from "./constants";
import { idbGet, idbPut } from "./idb";

let preferSession = false;

const TRANSIENT_KEYS = new Set(["sessionHydrated", "restoredFromSession", "miniSidenav", "headerSidenav", "mobile", "firstLoadPending"]);

/**
 * True after a snapshot was restored; GraphQL cache must not overwrite it.
 *
 * @returns {boolean}
 */
export const shouldPreferSession = () => preferSession;

/**
 * Marks whether restored working state should win over stale GraphQL cache.
 *
 * @param {boolean} value Prefer-session flag.
 * @returns {void}
 */
export const setPreferSession = (value) => {
  preferSession = !!value;
};

/**
 * True when the URL is the SPA shell rather than a real app page.
 *
 * @param {string} pathname Router pathname (basename already stripped).
 * @returns {boolean}
 */
export const isShellPath = (pathname) => {
  const path = String(pathname || "");
  return path === "/" || path === "/index.html" || path.endsWith("/index.html");
};

/**
 * True when this document load is a refresh.
 *
 * @returns {boolean}
 */
export const isReloadNavigation = () => {
  if (typeof performance === "undefined") return false;
  const entry = performance.getEntriesByType && performance.getEntriesByType("navigation")[0];
  if (entry && entry.type) return entry.type === "reload";
  return typeof performance.navigation !== "undefined" && performance.navigation.type === 1;
};

/**
 * Reads the last saved in-app path from sessionStorage.
 *
 * @returns {string}
 */
export const readLastPath = () => {
  if (typeof sessionStorage === "undefined") return "";
  return sessionStorage.getItem(LAST_PATH_KEY) || "";
};

/**
 * Remembers the current in-app path for the next refresh.
 *
 * @param {string} path Pathname + search + hash.
 * @returns {void}
 */
export const writeLastPath = (path) => {
  if (typeof sessionStorage === "undefined") return;
  const value = String(path || "");
  if (!value || isShellPath(value.split("?")[0].split("#")[0])) return;
  sessionStorage.setItem(LAST_PATH_KEY, value);
};

/**
 * Picks serializable working state, dropping chrome/hydration flags.
 *
 * @param {Object} state App store.
 * @returns {Object}
 */
export const pickPersistedState = (state) => {
  const next = {};
  Object.keys(state || {}).forEach((key) => {
    if (!TRANSIENT_KEYS.has(key)) next[key] = state[key];
  });
  return next;
};

/**
 * Writes the current path and store snapshot.
 *
 * @param {Object} state App store.
 * @param {string} [path] Current location.
 * @returns {Promise<void>}
 */
export const saveSession = async (state, path) => {
  try {
    const route = path || readLastPath();
    await idbPut(STORE_SESSION, {
      key: SESSION_RECORD_KEY,
      path: route,
      userId: readJwtUserId(),
      state: pickPersistedState(state),
      savedAt: Date.now(),
    });
  } catch {
    /* quota / private mode */
  }
};

/**
 * Loads the working session for the signed-in user.
 *
 * @returns {Promise<{ path: string, state: Object, savedAt: number }|null>}
 */
export const loadSession = async () => {
  try {
    const row = await idbGet(STORE_SESSION, SESSION_RECORD_KEY);
    if (!row || !row.state) return null;
    if (row.userId && row.userId !== readJwtUserId()) return null;
    return row;
  } catch {
    return null;
  }
};
